<template>
  <div class="min-h-screen bg-gray-950 p-6 bg-grid-pattern">
    <!-- Background effects -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-yellow/10 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="relative max-w-7xl mx-auto space-y-6">
      <!-- Header -->
      <div class="card">
        <div class="px-6 py-4 flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div
              class="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 via-accent-red to-accent-yellow flex items-center justify-center"
            >
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-white font-display">Партнёрская программа</h1>
              <p class="text-gray-400">Зарабатывайте {{ commissionRate }}% с каждого платежа приглашённых клиентов</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>

      <template v-else>
        <!-- Referral Link Card -->
        <div class="card bg-gradient-to-r from-primary-500/10 via-accent-red/10 to-accent-yellow/10 border-primary-500/30">
          <div class="px-6 py-6">
            <h3 class="text-lg font-semibold text-white mb-4">Ваша реферальная ссылка</h3>
            <div class="flex flex-row flex-nowrap items-center gap-4">
              <div class="flex-1 min-w-0">
                <div class="input-field flex items-center justify-between bg-gray-900/50 cursor-pointer" @click="copyLink">
                  <span class="text-gray-300 truncate">{{ stats?.referral_url }}</span>
                  <svg class="w-5 h-5 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <button @click="copyLink" class="btn-primary whitespace-nowrap inline-block">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {{ copied ? 'Скопировано!' : 'Копировать' }}
              </button>
            </div>
            <p class="text-sm text-gray-400 mt-3">
              Ваш код: <span class="text-primary-500 font-mono font-bold">{{ stats?.referral_code }}</span>
            </p>
          </div>
        </div>

        <!-- Stats Cards -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
          <!-- Total Referrals -->
          <div class="card">
            <div class="p-6">
              <div class="flex items-center">
                <div class="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div class="ml-4">
                  <p class="text-sm text-gray-400">Всего рефералов</p>
                  <p class="text-2xl font-bold text-white font-display">{{ stats?.stats?.total_referrals || 0 }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Active Referrals -->
          <div class="card">
            <div class="p-6">
              <div class="flex items-center">
                <div class="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="ml-4">
                  <p class="text-sm text-gray-400">Активных</p>
                  <p class="text-2xl font-bold text-white font-display">{{ stats?.stats?.active_referrals || 0 }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Pending Referrals -->
          <div class="card">
            <div class="p-6">
              <div class="flex items-center">
                <div class="w-12 h-12 bg-accent-yellow/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-accent-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="ml-4">
                  <p class="text-sm text-gray-400">Ожидают оплаты</p>
                  <p class="text-2xl font-bold text-white font-display">{{ stats?.stats?.pending_referrals || 0 }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Conversions -->
          <div class="card">
            <div class="p-6">
              <div class="flex items-center">
                <div class="w-12 h-12 bg-accent-red/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-accent-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div class="ml-4">
                  <p class="text-sm text-gray-400">Конверсий</p>
                  <p class="text-2xl font-bold text-white font-display">{{ stats?.stats?.conversions || 0 }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Balance Card -->
        <div class="card">
          <div class="px-6 py-4 border-b border-gray-800">
            <h3 class="text-lg font-semibold text-white font-display">💰 Баланс</h3>
          </div>
          <div class="px-6 py-6">
            <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <p class="text-sm text-gray-400 mb-1">Доступно к выводу</p>
                <p class="text-3xl font-bold text-gradient font-display">{{ formatMoney(stats?.balance?.available || 0) }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-400 mb-1">Ожидает начисления</p>
                <p class="text-2xl font-bold text-accent-yellow font-display">{{ formatMoney(stats?.balance?.pending || 0) }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-400 mb-1">Всего заработано</p>
                <p class="text-2xl font-bold text-white font-display">{{ formatMoney(stats?.balance?.total_earned || 0) }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-400 mb-1">Выплачено</p>
                <p class="text-2xl font-bold text-gray-400 font-display">{{ formatMoney(stats?.balance?.paid_out || 0) }}</p>
              </div>
            </div>

            <div class="mt-6 pt-6 border-t border-gray-800 flex flex-row flex-wrap items-center justify-between gap-4">
              <p class="text-sm text-gray-400">
                Минимальная сумма для вывода: <span class="text-white font-semibold">{{ formatMoney(stats?.min_payout_amount || 5000) }}</span>
              </p>
              <button
                @click="showPayoutModal = true"
                :disabled="!canRequestPayout"
                class="btn-primary inline-block"
                :class="{ 'opacity-50 cursor-not-allowed': !canRequestPayout }"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Вывести средства
              </button>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="card">
          <div class="px-6 py-4 border-b border-gray-800">
            <div class="flex space-x-4">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                @click="activeTab = tab.id"
                class="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                :class="activeTab === tab.id ? 'bg-primary-500/20 text-primary-500' : 'text-gray-400 hover:text-white hover:bg-gray-800'"
              >
                {{ tab.label }}
              </button>
            </div>
          </div>

          <!-- Referrals Table -->
          <div v-if="activeTab === 'referrals'" class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-900/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Тариф</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Дата</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Заработано</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-800">
                <tr v-for="referral in referrals" :key="referral.id" class="hover:bg-gray-900/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{{ referral.email }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="px-2 py-1 text-xs rounded-full" :class="getPlanClass(referral.plan)">
                      {{ referral.plan }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{{ referral.registered_at }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="px-2 py-1 text-xs rounded-full" :class="getStatusClass(referral.status)">
                      {{ getStatusLabel(referral.status) }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">{{ formatMoney(referral.total_earned) }}</td>
                </tr>
                <tr v-if="referrals.length === 0">
                  <td colspan="5" class="px-6 py-12 text-center text-gray-500">
                    Пока нет рефералов. Поделитесь своей ссылкой!
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Earnings Table -->
          <div v-if="activeTab === 'earnings'" class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-900/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Реферал</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Сумма платежа</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Комиссия</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Дата</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-800">
                <tr v-for="earning in earnings" :key="earning.id" class="hover:bg-gray-900/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{{ earning.referral_email }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{{ formatMoney(earning.payment_amount) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">{{ formatMoney(earning.commission_amount) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{{ earning.created_at }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="px-2 py-1 text-xs rounded-full" :class="getEarningStatusClass(earning.status)">
                      {{ getEarningStatusLabel(earning.status) }}
                    </span>
                  </td>
                </tr>
                <tr v-if="earnings.length === 0">
                  <td colspan="5" class="px-6 py-12 text-center text-gray-500">
                    Пока нет начислений
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Payouts Table -->
          <div v-if="activeTab === 'payouts'" class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-900/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Сумма</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Способ</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Дата</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Действия</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-800">
                <tr v-for="payout in payouts" :key="payout.id" class="hover:bg-gray-900/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">{{ payout.formatted_amount }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{{ getPaymentMethodLabel(payout.payment_method) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{{ payout.created_at }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="px-2 py-1 text-xs rounded-full" :class="getPayoutStatusClass(payout.status)">
                      {{ payout.status_label }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <button
                      v-if="payout.status === 'pending' || payout.status === 'processing'"
                      @click="cancelPayout(payout.id)"
                      class="text-red-400 hover:text-red-300 text-sm"
                    >
                      Отменить
                    </button>
                  </td>
                </tr>
                <tr v-if="payouts.length === 0">
                  <td colspan="5" class="px-6 py-12 text-center text-gray-500">
                    Пока нет выплат
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Info Card -->
        <div class="card">
          <div class="px-6 py-4 border-b border-gray-800">
            <h3 class="text-lg font-semibold text-white font-display">📋 Как это работает</h3>
          </div>
          <div class="px-6 py-6">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div class="flex items-start gap-4">
                <div class="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <span class="text-primary-500 font-bold">1</span>
                </div>
                <div>
                  <h4 class="font-semibold text-white mb-1">Делитесь ссылкой</h4>
                  <p class="text-sm text-gray-400">Отправьте реферальную ссылку друзьям и коллегам</p>
                </div>
              </div>
              <div class="flex items-start gap-4">
                <div class="w-10 h-10 rounded-xl bg-accent-red/20 flex items-center justify-center flex-shrink-0">
                  <span class="text-accent-red font-bold">2</span>
                </div>
                <div>
                  <h4 class="font-semibold text-white mb-1">Они регистрируются</h4>
                  <p class="text-sm text-gray-400">Новые пользователи переходят по ссылке и регистрируются</p>
                </div>
              </div>
              <div class="flex items-start gap-4">
                <div class="w-10 h-10 rounded-xl bg-accent-yellow/20 flex items-center justify-center flex-shrink-0">
                  <span class="text-accent-yellow font-bold">3</span>
                </div>
                <div>
                  <h4 class="font-semibold text-white mb-1">Получайте {{ commissionRate }}%</h4>
                  <p class="text-sm text-gray-400">Вы получаете {{ commissionRate }}% от их платежей в течение {{ commissionPeriod }} месяцев</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Payout Modal -->
    <Teleport to="body">
      <div v-if="showPayoutModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div class="fixed inset-0 bg-black/75 transition-opacity" @click="showPayoutModal = false"></div>
          
          <div class="relative inline-block w-full max-w-md p-6 my-8 text-left align-middle bg-gray-900 border border-gray-800 rounded-2xl shadow-xl transform transition-all">
            <h3 class="text-xl font-bold text-white font-display mb-6">Запрос на вывод средств</h3>
            
            <form @submit.prevent="submitPayout" class="space-y-4">
              <div>
                <label class="form-label">Сумма (₽)</label>
                <input
                  v-model="payoutForm.amount"
                  type="number"
                  :min="stats?.min_payout_amount || 5000"
                  :max="stats?.balance?.available || 0"
                  class="input-field"
                  required
                />
                <p class="text-xs text-gray-500 mt-1">
                  Доступно: {{ formatMoney(stats?.balance?.available || 0) }}
                </p>
              </div>

              <div>
                <label class="form-label">Способ выплаты</label>
                <select v-model="payoutForm.payment_method" class="input-field" required>
                  <option value="card">Банковская карта</option>
                  <option value="bank_transfer">Банковский перевод</option>
                </select>
              </div>

              <div v-if="payoutForm.payment_method === 'card'">
                <label class="form-label">Номер карты</label>
                <input
                  v-model="payoutForm.payment_details.card_number"
                  type="text"
                  class="input-field"
                  placeholder="1234 5678 9012 3456"
                  required
                />
              </div>

              <template v-if="payoutForm.payment_method === 'bank_transfer'">
                <div>
                  <label class="form-label">Название банка</label>
                  <input
                    v-model="payoutForm.payment_details.bank_name"
                    type="text"
                    class="input-field"
                    required
                  />
                </div>
                <div>
                  <label class="form-label">БИК</label>
                  <input
                    v-model="payoutForm.payment_details.bik"
                    type="text"
                    class="input-field"
                    required
                  />
                </div>
                <div>
                  <label class="form-label">Номер счёта</label>
                  <input
                    v-model="payoutForm.payment_details.account_number"
                    type="text"
                    class="input-field"
                    required
                  />
                </div>
              </template>

              <div>
                <label class="form-label">ФИО получателя</label>
                <input
                  v-model="payoutForm.payment_details.recipient_name"
                  type="text"
                  class="input-field"
                  required
                />
              </div>

              <div class="flex gap-4 pt-4">
                <button type="button" @click="showPayoutModal = false" class="btn-secondary flex-1">
                  Отмена
                </button>
                <button type="submit" class="btn-primary flex-1" :disabled="payoutLoading">
                  {{ payoutLoading ? 'Отправка...' : 'Отправить заявку' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import api from '../services/api';
import { useNotifications } from '../composables/useNotifications';

export default {
  name: 'Referrals',
  setup() {
    const { success, error } = useNotifications();
    return { success, error };
  },
  data() {
    return {
      loading: true,
      stats: null,
      referrals: [],
      earnings: [],
      payouts: [],
      activeTab: 'referrals',
      copied: false,
      showPayoutModal: false,
      payoutLoading: false,
      payoutForm: {
        amount: 5000,
        payment_method: 'card',
        payment_details: {
          card_number: '',
          bank_name: '',
          bik: '',
          account_number: '',
          recipient_name: '',
        },
      },
      tabs: [
        { id: 'referrals', label: '📋 Рефералы' },
        { id: 'earnings', label: '💰 Начисления' },
        { id: 'payouts', label: '💳 Выплаты' },
      ],
    };
  },
  computed: {
    canRequestPayout() {
      return (this.stats?.balance?.available || 0) >= (this.stats?.min_payout_amount || 5000);
    },
    commissionRate() {
      return this.stats?.commission_rate || 20;
    },
    commissionPeriod() {
      return this.stats?.commission_period_months || 12;
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadStats(),
          this.loadReferrals(),
          this.loadEarnings(),
          this.loadPayouts(),
        ]);
      } catch (err) {
        console.error('Failed to load referral data:', err);
        this.error('Ошибка', 'Не удалось загрузить данные партнёрской программы');
      } finally {
        this.loading = false;
      }
    },
    async loadStats() {
      const response = await api.get('/referral');
      if (response.data.success) {
        this.stats = response.data.data;
      }
    },
    async loadReferrals() {
      const response = await api.get('/referral/referrals');
      if (response.data.success) {
        this.referrals = response.data.data.items;
      }
    },
    async loadEarnings() {
      const response = await api.get('/referral/earnings');
      if (response.data.success) {
        this.earnings = response.data.data.items;
      }
    },
    async loadPayouts() {
      const response = await api.get('/referral/payouts');
      if (response.data.success) {
        this.payouts = response.data.data.items;
      }
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.stats?.referral_url);
        this.copied = true;
        this.success('Скопировано', 'Реферальная ссылка скопирована в буфер обмена');
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        this.error('Ошибка', 'Не удалось скопировать ссылку');
      }
    },
    async submitPayout() {
      this.payoutLoading = true;
      try {
        const response = await api.post('/referral/payout', this.payoutForm);
        if (response.data.success) {
          this.success('Успех', 'Заявка на вывод успешно создана');
          this.showPayoutModal = false;
          this.loadStats();
          this.loadPayouts();
          this.resetPayoutForm();
        }
      } catch (err) {
        this.error('Ошибка', err.response?.data?.message || 'Не удалось создать заявку');
      } finally {
        this.payoutLoading = false;
      }
    },
    async cancelPayout(id) {
      if (!confirm('Вы уверены, что хотите отменить заявку на вывод?')) return;

      try {
        const response = await api.post(`/referral/payout/${id}/cancel`);
        if (response.data.success) {
          this.success('Успех', 'Заявка отменена');
          this.loadStats();
          this.loadPayouts();
        }
      } catch (err) {
        this.error('Ошибка', err.response?.data?.message || 'Не удалось отменить заявку');
      }
    },
    resetPayoutForm() {
      this.payoutForm = {
        amount: 5000,
        payment_method: 'card',
        payment_details: {
          card_number: '',
          bank_name: '',
          bik: '',
          account_number: '',
          recipient_name: '',
        },
      };
    },
    formatMoney(amount) {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    },
    getPlanClass(plan) {
      const classes = {
        'Free': 'bg-gray-700 text-gray-300',
        'Starter': 'bg-primary-500/20 text-primary-500',
        'Pro': 'bg-accent-red/20 text-accent-red',
        'Enterprise': 'bg-accent-yellow/20 text-accent-yellow',
      };
      return classes[plan] || 'bg-gray-700 text-gray-300';
    },
    getStatusClass(status) {
      const classes = {
        'pending': 'bg-yellow-500/20 text-yellow-500',
        'active': 'bg-green-500/20 text-green-500',
        'cancelled': 'bg-red-500/20 text-red-500',
      };
      return classes[status] || 'bg-gray-700 text-gray-300';
    },
    getStatusLabel(status) {
      const labels = {
        'pending': 'Ожидает оплаты',
        'active': 'Активен',
        'cancelled': 'Отменён',
      };
      return labels[status] || status;
    },
    getEarningStatusClass(status) {
      const classes = {
        'pending': 'bg-yellow-500/20 text-yellow-500',
        'available': 'bg-green-500/20 text-green-500',
        'paid': 'bg-gray-700 text-gray-300',
      };
      return classes[status] || 'bg-gray-700 text-gray-300';
    },
    getEarningStatusLabel(status) {
      const labels = {
        'pending': 'Обрабатывается',
        'available': 'Доступно',
        'paid': 'Выплачено',
      };
      return labels[status] || status;
    },
    getPayoutStatusClass(status) {
      const classes = {
        'pending': 'bg-yellow-500/20 text-yellow-500',
        'processing': 'bg-blue-500/20 text-blue-500',
        'completed': 'bg-green-500/20 text-green-500',
        'failed': 'bg-red-500/20 text-red-500',
        'cancelled': 'bg-gray-700 text-gray-300',
      };
      return classes[status] || 'bg-gray-700 text-gray-300';
    },
    getPaymentMethodLabel(method) {
      const labels = {
        'card': 'Карта',
        'bank_transfer': 'Банковский перевод',
      };
      return labels[method] || method;
    },
  },
};
</script>
